import React, { useEffect, useMemo, useState, useCallback } from 'react';
import './WithdrawalManager.css';
import { useNavigate, useLocation } from 'react-router-dom';
import backIcon from '../../assets/backspace-icon.png';
import api from '../../api';

export default function WithdrawalManager() {
  const navigate = useNavigate();
  const { search } = useLocation();

  // Route network (authoritative)
  const network = useMemo(() => {
    const p = new URLSearchParams(search);
    const n = (p.get('network') || '').toLowerCase();
    return n === 'testnet' || n === 'mainnet' ? n : 'mainnet';
  }, [search]);

  const [withdrawals, setWithdrawals] = useState([]); // merged pending + recent
  const [filter, setFilter] = useState('all'); // all | pending | completed | rejected | cancelled
  const [loading, setLoading] = useState(true);

  // 👉 pill click updates the URL (source of truth)
  const switchNetwork = useCallback((target) => {
    const t = target === 'testnet' ? 'testnet' : 'mainnet';
    navigate(`/admin/withdrawals?network=${t}`);
  }, [navigate]);

  useEffect(() => {
    const fetchWithdrawals = async () => {
      setLoading(true);
      try {
        // Canonical backend path in your app: /api/withdrawals
        const { data } = await api.get(`/withdrawals?network=${network}`);
        const merged = [
          ...(data?.pending || data?.withdrawals || []), // supports grouped or legacy list
          ...(data?.recent || []),
        ];
        setWithdrawals(merged);
      } catch (err) {
        console.error('❌ Failed to fetch withdrawals:', err);
        setWithdrawals([]);
      } finally {
        setLoading(false);
      }
    };
    fetchWithdrawals();
  }, [network]);

  // Filter logic
  const filteredWithdrawals = withdrawals.filter(w => (filter === 'all' ? true : w.status === filter));

  return (
    <div className="withdrawal-manager">
      <div className="top-bar">
        <img src={backIcon} alt="Back" onClick={() => navigate('/admin')} />
        <h2>Withdrawal Requests</h2>
      </div>

      {/* 🌐 Network pill switcher */}
      <div className="network-pills" role="tablist" aria-label="Select network">
        <button
          role="tab"
          aria-selected={network === 'testnet'}
          className={`pill ${network === 'testnet' ? 'active' : ''}`}
          onClick={() => switchNetwork('testnet')}
        >
          Testnet
        </button>
        <button
          role="tab"
          aria-selected={network === 'mainnet'}
          className={`pill ${network === 'mainnet' ? 'active' : ''}`}
          onClick={() => switchNetwork('mainnet')}
        >
          Mainnet
        </button>
      </div>

      {/* Status filter */}
      <div className="withdrawal-filter-bar">
        <button className={filter === 'all' ? 'active' : ''} onClick={() => setFilter('all')}>All</button>
        <button className={filter === 'pending' ? 'active' : ''} onClick={() => setFilter('pending')}>Pending</button>
        <button className={filter === 'completed' ? 'active' : ''} onClick={() => setFilter('completed')}>Completed</button>
        <button className={filter === 'rejected' ? 'active' : ''} onClick={() => setFilter('rejected')}>Rejected</button>
        <button className={filter === 'cancelled' ? 'active' : ''} onClick={() => setFilter('cancelled')}>Cancelled</button>
      </div>

      {loading ? (
        <div className="withdrawal-list"><p style={{ color: '#888' }}>Loading…</p></div>
      ) : (
        <div className="withdrawal-list">
          {filteredWithdrawals.map((w) => (
            <div
              key={w._id}
              className="withdrawal-card"
              onClick={() => navigate(`/admin/withdrawals/${w._id}?network=${network}`)}
            >
              <div className="withdrawal-card-row">
                <h3>{w.username}</h3>
                <span className={`status ${w.status}`}>{w.status}</span>
              </div>
              <div className="withdrawal-card-row">
                <p>{Number(w.amount).toFixed(2)} Pi</p>
                <small>{w.createdAt ? new Date(w.createdAt).toLocaleString() : ''}</small>
              </div>
            </div>
          ))}
          {filteredWithdrawals.length === 0 && (
            <p style={{ color: '#888' }}>No items.</p>
          )}
        </div>
      )}
    </div>
  );
}
